import { ReactComponent as OwnerIcon } from 'assets/icons/person_pin.svg';
import { ReactComponent as StatusIcon } from 'assets/icons/status-icon.svg';
import { FIELD_TYPES } from 'constants/fieldTypes';
import {
  PAGE_WHITELISTED_PARAMS,
  PARAMS,
  SUCCESSION_POOLS_DEFAULT_PARAMS,
} from 'constants/pages';
import {
  SUCCESSION_POOL_FIELDS,
  SUCCESSION_POOL_FILTER,
  SUCCESSION_POOL_INDICATOR_KEYS,
  SUCCESSION_POOL_STATUS,
} from 'constants/succession';
import {
  isArray,
  prepareFilterWithAllOption,
  trimString,
} from 'utility/helpers';
import { parseQueryParams, prepareFilterableUsers } from 'utility/uiUtils';
import {
  forbiddenCharacters,
  isEmpty,
  validateLength,
} from 'utility/validation';

const { SEARCH, PAGE } = PARAMS;
const { STATUS, OWNER } = SUCCESSION_POOL_FILTER;
const { TEXT, SELECT } = FIELD_TYPES;
const NAME_MAX_LENGTH = 128;
const DESCRIPTION_MAX_LENGTH = 1000;

export const INITIAL_SUCCESSION_POOL_FILTERS = {
  [STATUS]: [],
  [OWNER]: [],
};

const textColumn = (
  id,
  title,
  rowKey,
  minWidth,
  maxWidth = '1fr',
  isHighlighted = false,
  hasConditionalTooltip = false
) => ({
  id,
  title,
  rowKey,
  isSortingDisabled: true,
  minWidth,
  maxWidth,
  isHighlighted,
  hasConditionalTooltip,
});

export const prepareSuccessionPoolFiltersFromLocation = search => {
  const params = parseQueryParams(
    search || '',
    PAGE_WHITELISTED_PARAMS.SUCCESSION_POOLS
  );

  return {
    search: params[SEARCH] ? decodeURIComponent(params[SEARCH]) : '',
    page: params[PAGE] || SUCCESSION_POOLS_DEFAULT_PARAMS[PAGE],
    filters: {
      [STATUS]: params[STATUS]
        ? [...(isArray(params[STATUS]) ? params[STATUS] : [params[STATUS]])]
        : [],
      [OWNER]: params[OWNER]
        ? [...(isArray(params[OWNER]) ? params[OWNER] : [params[OWNER]])]
        : [],
    },
  };
};

export const prepareSuccessionPoolFilters = (labels, employees) => {
  return [
    {
      id: STATUS,
      name: labels.status,
      icon: StatusIcon,
      isSingleSelect: true,
      items: prepareFilterWithAllOption(labels.all, [
        { id: SUCCESSION_POOL_STATUS.ACTIVE, name: labels.active },
        { id: SUCCESSION_POOL_STATUS.ARCHIVED, name: labels.archived },
      ]),
    },
    {
      id: OWNER,
      name: labels.owner,
      icon: OwnerIcon,
      isSingleSelect: true,
      items: prepareFilterWithAllOption(
        labels.all,
        prepareFilterableUsers(employees)
      ),
    },
  ];
};

export const prepareSuccessionPoolTableHeaders = labels => [
  textColumn('name', labels.name, 'name', 180, '1fr', true, true),
  textColumn('owner', labels.owner, 'owner', 220),
  textColumn(
    'description',
    labels.description,
    'description',
    180,
    '1fr',
    false,
    true
  ),
  textColumn('members', labels.members, 'members', 90),
  textColumn('status', labels.status, 'statusComponent', 110, 140),
];

export const prepareSuccessionPoolTableRows = (
  labels,
  pools,
  renderUser,
  renderActions,
  renderStatus
) =>
  (pools || []).map(pool => {
    return {
      id: pool.id,
      name: pool.name,
      owner: pool.owner ? renderUser(pool.owner) : '-',
      members: pool.members_count,
      description: pool.description || '-',
      status: pool?.status,
      statusComponent: pool?.status
        ? renderStatus({
            key: SUCCESSION_POOL_INDICATOR_KEYS[pool.status],
            name: labels?.statuses?.[pool.status] || '-',
            value: pool.status,
          })
        : '-',
      actions: renderActions(pool),
      rowLink: {
        to: `/succession/pools/${pool.id}`,
        state: { title: pool?.name || '' },
      },
    };
  });

export const prepareSuccessionPoolFormFields = () => [
  {
    name: SUCCESSION_POOL_FIELDS.NAME,
    type: TEXT,
    required: true,
    validators: [
      { type: 'required', validator: isEmpty },
      { type: 'forbiddenCharacters', validator: forbiddenCharacters },
      {
        type: 'nameMaxLength',
        validator: value => validateLength(value || '', 0, NAME_MAX_LENGTH),
      },
    ],
  },
  {
    name: SUCCESSION_POOL_FIELDS.DESCRIPTION,
    type: TEXT,
    multiline: true,
    rows: 4,
    validators: [
      { type: 'forbiddenCharacters', validator: forbiddenCharacters },
      {
        type: 'descriptionMaxLength',
        validator: value =>
          validateLength(value || '', 0, DESCRIPTION_MAX_LENGTH),
      },
    ],
  },
  {
    name: SUCCESSION_POOL_FIELDS.OWNER,
    type: SELECT,
    optionsKey: 'employees',
    isUser: true,
    required: true,
    shouldReturnOption: true,
    parser: { value: 'id', label: 'first_name' },
    validators: [
      {
        type: 'required',
        validator: value => {
          return isEmpty(value);
        },
      },
    ],
  },
];

export const prepareSuccessionPoolFormInitialData = pool => {
  return {
    id: pool?.id,
    [SUCCESSION_POOL_FIELDS.NAME]: pool?.name || '',
    [SUCCESSION_POOL_FIELDS.DESCRIPTION]: pool?.description || '',
    [SUCCESSION_POOL_FIELDS.OWNER]: pool?.owner || null,
  };
};

export const prepareSuccessionPoolValues = values => {
  return {
    [SUCCESSION_POOL_FIELDS.NAME]: trimString(
      values[SUCCESSION_POOL_FIELDS.NAME] || ''
    ),
    [SUCCESSION_POOL_FIELDS.DESCRIPTION]: trimString(
      values[SUCCESSION_POOL_FIELDS.DESCRIPTION] || ''
    ),
    [SUCCESSION_POOL_FIELDS.OWNER]: values[SUCCESSION_POOL_FIELDS.OWNER].id,
  };
};
